import type { SWRConfiguration } from "swr";

/**
 * Builds the SWR config for our fetch hooks.
 *
 * `onError` is spread in only when a callback is provided. Setting
 * `onError: undefined` explicitly would override SWR's default `onError` (a
 * no-op) because SWR merges configs with a plain object spread, leaving
 * `config.onError` undefined and throwing `onError is not a function` on the
 * next fetch failure. Keeping this in one place stops the two fetch hooks from
 * drifting apart.
 */
export function buildSwrConfig<Data, Err>(
  base: SWRConfiguration<Data, Err>,
  swrOptions: SWRConfiguration<Data, Err> | undefined,
  onError: ((error: Err) => void) | undefined,
): SWRConfiguration<Data, Err> {
  return {
    ...base,
    ...swrOptions,
    ...(onError ? { onError } : {}),
  };
}

/**
 * Fetch defaults shared by every SWR-backed hook: a failed request is surfaced
 * rather than retried, and data is not silently re-fetched on focus or
 * reconnect.
 */
export const DEFAULT_SWR_OPTIONS: SWRConfiguration = {
  shouldRetryOnError: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};
